import { useEffect, useRef } from 'react';
import { fetchAllProjectProblems } from './ocrApi';

/**
 * 주소로 건너온 검수 대상(`?review=` — ocrReviewLink)을 한 번 연다. 문제 v2 「OCR 등록」 배지를 ⌘·Ctrl 로 눌러
 * 새 탭에 연 길이다(그냥 누르면 문제 화면 위에 모달이 뜬다 — ProblemHubV2/OcrSourceReviewModal).
 *
 * 그냥 번호로 열지 않고 **전체 순서에서의 위치를 찾아** 연다. 검수 모달의 이전·다음은 지금 쪽(20건)만 보고
 * 계산하므로, 첫 쪽에 없는 문제를 위치 없이 열면 그 계산이 무관한 문제로 튄다(useOcrProblemList.openById 주석).
 * 인덱스 지도가 넘기는 위치와 같은 값이다 — 같은 전량 조회의 순번.
 *
 * - 상세가 서기 전(detailStatus null)에는 기다린다. 매칭 전(300 미만)이면 열 것이 없으니 열지 않고 소비만 한다.
 * - 위치를 못 찾거나 전량 조회가 실패해도 연다 — 상세는 번호로 읽힌다. 잃는 것은 이전·다음의 기준뿐이다.
 * - 연 뒤 onOpened 로 알린다 — 부르는 쪽이 주소에서 번호를 지워, 모달을 닫고 새로고침해도 다시 열리지 않게 한다.
 * - 그 사이 패널이 내려가면(프로젝트 전환·화면 이탈) 아무것도 하지 않는다. 주소를 고치는 onOpened 가
 *   이미 떠난 화면의 주소를 덮지 않게 하려는 것이다.
 *
 * @param open (ocrProbId, allIndex|null) => void — 패널의 openForOcr(검수 모드로 연다)
 */
export default function useReviewDeepLink({ reviewId, projectId, detailStatus, open, onOpened, getAccessToken }) {
  // 매 렌더 새로 만들어지는 함수를 의존성에 넣으면 조회 도중 효과가 다시 돌아 같은 대상을 두 번 연다 — ref 로 최신 것만 쥔다
  const openRef = useRef(open);
  const onOpenedRef = useRef(onOpened);
  useEffect(() => {
    openRef.current = open;
    onOpenedRef.current = onOpened;
  });

  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doneRef = useRef(null); // 이미 처리한 대상 — 같은 번호로 두 번 열지 않는다
  useEffect(() => {
    if (reviewId == null) {
      doneRef.current = null;
      return;
    }
    if (detailStatus == null || doneRef.current === reviewId) return;
    doneRef.current = reviewId;
    if (detailStatus < 300) {
      onOpenedRef.current?.();
      return;
    }
    (async () => {
      let index = null;
      try {
        const { items } = await fetchAllProjectProblems(projectId, getAccessToken);
        const i = items.findIndex((p) => String(p.ocr_prob_id) === String(reviewId));
        if (i >= 0) index = i;
      } catch (e) {
        // 위치 없이 연다 — 위 머리 주석
      }
      if (!mountedRef.current) return;
      openRef.current?.(reviewId, index);
      onOpenedRef.current?.();
    })();
  }, [reviewId, detailStatus, projectId, getAccessToken]);
}
